import { Alert, Divider, Group, Image, Title } from "@mantine/core";
import { IconCheck } from "@tabler/icons-react";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { optInGqlx } from "shared/api/gqlx";
import { Content } from "shared/components/global/Content";
import { AddEudPanel } from "shared/components/opt-in/AddEudPanel";
import { ContactMethodsPanel } from "shared/components/opt-in/ContactMethodsPanel";
import { createOptInUserFormState, OptInUserFormProvider, useOptInUserForm } from "shared/components/opt-in/context";
import { DeleteAccountPanel } from "shared/components/opt-in/DeleteAccountPanel";
import { LanguageAndNameSelector } from "shared/components/opt-in/LanguageAndNameSelector";
import { TopicsPanel } from "shared/components/opt-in/TopicsPanel";
import { useCustomer } from "shared/graphql/cust";
import { features } from "shared/graphql/features";
import { graphql } from "src/gql";

const Page = () => {
    const { userId } = Route.useParams();
    const hasTopics = features.enabled("HAS_TOPICS");

    const {
        data: { user },
    } = useSuspenseQuery(query.user(userId));

    const { data: customer } = useCustomer();

    const optInUserForm = useOptInUserForm({ initialValues: createOptInUserFormState({ user, customer }) });

    return (
        <OptInUserFormProvider form={optInUserForm}>
            <Content paper>
                <Group w="100%" justify="center">
                    <Image
                        my="md"
                        src={`${import.meta.env.VITE_API_BASE_URL}/customers/${customer.id}/icon`}
                        fallbackSrc="/bryx.svg"
                        style={{ maxWidth: 50, maxHeight: 50 }}
                    />
                    <Title order={1}>{customer.name}</Title>
                </Group>
                <Divider mx="-sm" />

                <Alert title="You are signed up for alerts!" w="fit-content" icon={<IconCheck />}>
                    Your alert subscriptions are active and your contact information has been verified.
                    <br />
                    You may continue managing your account or close this page.
                </Alert>

                <LanguageAndNameSelector />

                <ContactMethodsPanel />
                <AddEudPanel />
                {hasTopics && <TopicsPanel />}

                <Divider />
                <DeleteAccountPanel />
            </Content>
        </OptInUserFormProvider>
    );
};

const query = {
    user: (userId: string) =>
        queryOptions({
            queryKey: ["optin-user", userId],
            queryFn: () =>
                optInGqlx(userId)(
                    graphql(`
                        query OptInUser($userId: UUID!) {
                            user(userId: $userId) {
                                id
                                name
                                language
                                euds {
                                    id
                                    type
                                    value
                                    isVerified
                                }
                                topics {
                                    ...TopicLike
                                }
                            }
                        }
                    `),
                    { userId },
                ),
        }),
};

/** @public */
export const Route = createFileRoute("/opt-in/_layout/$userId")({
    component: Page,
});
